import React from "react";
import {
  Box,
  Divider,
  Paper,
  Popover,
  Avatar,
  Typography,
  IconButton,
} from "@mui/material";
import MailOpened from "../../assets/images/icons/mailOpened.png";
import { PrimaryButtonText } from "../ui/Button";
import notificationsServices from "../../services/api/service/notificationService";
import { useDispatch, useSelector } from "react-redux";
import {
  notificationSelector,
  setNotification,
  setUnreadCount,
} from "../../services/redux/slices/notificationSlice";
import { Fragment, useEffect } from "react";
import { authSelector } from "../../services/redux/slices/authSlice";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../services/redux/store";
import { setDocumentDiscardModal } from "../../services/redux/slices/documentFormSlice";
import { encrypt } from "../../utils/crypto";
import CloseIcon from "@mui/icons-material/Close";
import FormattedDate from "./FormattedDate";

type NotificationsDropDownProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
};

type NotificationItem = {
  id: number;
  avatar?: string;
  initials?: string;
  name?: string;
  message?: string;
  created_at: string;
};

const NotificationsDropDown: React.FC<NotificationsDropDownProps> = ({
  open,
  anchorEl,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userProfile } = useSelector(authSelector);
  const businessTypeId = useSelector(
    (state: RootState) => state.auth.businessType?.id
  );

  const loanCategoryData = useSelector(
    (state: RootState) => state.auth.loanCategory
  );

  const { notificationData, loading } = useSelector(notificationSelector);

  const readNotification = async (id: number) => {
    try {
      const payload = { id: [id] };
      const res = await notificationsServices.readNotification(payload);
      if (res?.status === 200) {
        getNotificationsList();
      }
    } catch (error) {
      console.error("Read Notification API Error", error);
    }
  };

  const getNotificationsList = async () => {
    try {
      const bodyObj: any = {};
      let cifNumber = userProfile?.cifNumber;
      bodyObj.cifNumber = cifNumber;
      bodyObj.businessType = encrypt(businessTypeId);
      bodyObj.loanCategory = encrypt(
        loanCategoryData?.TermLoan[0]?.loanCategoryId
      );
      const res = await notificationsServices.getNotifications(bodyObj);
      if (res?.status === 200 && res?.data?.data) {
        const notificationsList = res.data.data.filter(
          (n: any) => n.readStatus === false
        );
        const unreadCount = res.data.pagination?.unreadNotificationsCounts;
        dispatch(setNotification(notificationsList));
        dispatch(setUnreadCount(unreadCount));
      }
    } catch (error) {
      console.error("Get notifications API error:", error);
    }
  };

  const selectedFormType = useSelector(
    (state: RootState) => state.documentFormSlice
  );

  const handleViewAll = () => {
    if (
      selectedFormType?.formType === "continue" &&
      selectedFormType?.isNewData
    ) {
      dispatch(setDocumentDiscardModal());
    } else {
      handleClose(); // Close the dropdown
      navigate("/notifications");
      notificationsServices.readNotification({
        readStatusAll: true,
      });
    }
  };

  const handleDeleteNotification = (id: number) => {
    // Implement your delete notification logic heres
  };

  useEffect(() => {
    if (open) {
      getNotificationsList();
    }
  }, []);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 70,
        horizontal: 1140,
      }}
      slotProps={{
        paper: {
          sx: {
            width: "340px",
            borderRadius: "8px",
            border: "1px solid #E6E6E6",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          },
        },
      }}
    >
      <Box className="notification-header-wrapper">
        <Box
          className="header"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
        >
          <Typography variant="h6" ml={1}>
            Notification
          </Typography>
          {/* <img src={MailOpened} alt="MailOpened" /> */}
        </Box>
        <Divider className="" />
        {loading ? (
          <Box className="notification-empty" sx={{ paddingBlock: "50px" }}>
            <Box component="h4">Loading...</Box>
          </Box>
        ) : (
          <Fragment>
            {notificationData?.length ? (
              <Box className="notification-list">
                {notificationData
                  .slice(0, 5)
                  .map((item: NotificationItem, index: number) => (
                    <Paper
                      elevation={0}
                      key={item.id}
                      sx={{
                        borderBottom:
                          index < notificationData.length - 1
                            ? "1px solid #E6E6E6"
                            : "none",
                        position: "relative",
                      }}
                    >
                      <Box display="flex" alignItems="center" p={1}>
                        {item.avatar ? (
                          <Avatar
                            alt={item.name ?? undefined}
                            src={item.avatar}
                          />
                        ) : item.initials ? (
                          <Avatar>{item.initials}</Avatar>
                        ) : (
                          <Avatar>
                            {/* Replace with the appropriate icon */}
                          </Avatar>
                        )}
                        <Box ml={2}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            onClick={() => readNotification(item.id)}
                            sx={{ cursor: "pointer" }}
                          >
                            {typeof item.message === "string" &&
                            item.message.length > 40
                              ? `${item.message.slice(0, 40)}...`
                              : item.message ?? ""}
                          </Typography>
                          <Typography
                            variant="caption"
                            className="notification-list-date"
                          >
                            <FormattedDate createdAt={item?.created_at} />
                          </Typography>
                        </Box>
                        <IconButton
                          sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            opacity: 0,
                            transition: "opacity 0.2s",
                            "&:hover": { opacity: 1 },
                          }}
                          onClick={() => handleDeleteNotification(item.id)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
              </Box>
            ) : (
              <Box className="notification-empty dashboard-container" p={2}>
                <Typography className="insidePageValue">
                  No Notification
                </Typography>
                <Typography className="insidePageLable">
                  When you have notification(s), you will see them here.
                </Typography>
              </Box>
            )}
            <Box sx={{ textAlign: "center" }}>
              <Divider className="" />
              <PrimaryButtonText onClick={handleViewAll}>
                View all notifications
              </PrimaryButtonText>
            </Box>
          </Fragment>
        )}
      </Box>
    </Popover>
  );
};

export default NotificationsDropDown;
